// AdminProfile — "your account" for both admin and agent. Redesigned
// in the new aesthetic with light + dark.
//
// Sections (single scroll, mobile-first):
//   1. Hero — large avatar + role chip + change-photo / remove
//   2. Identity — username + email (editable)
//   3. Password — current + new with strength check; revokes other sessions
//   4. Account info — member since / last login / last IP
//   5. Sign out — destructive
//
// All mutations hit /admin/auth/me (PUT), /admin/auth/me/avatar
// (POST/DELETE), /admin/auth/change-password (POST). On success the
// shell's auth context is refreshed so the new avatar / username
// shows up immediately in the top bar.
//
// Phase 4 UI overhaul (2026-05-15): mounted inside AccountHub as the
// "Profile" sub-tab — the hub already provides the outer max-w, the
// "Account" page title, and the sub-tab strip. This panel now drops
// its own outer wrapper + duplicate "My profile" page header. The
// gradient HERO card stays (it's the page's signature) but the
// surrounding rectangular cards switch to arena-tinted ring-1
// ring-inset surfaces matching every other Phase 2/3 card. Avatar
// FormData upload + password mutation flows are unchanged.

import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import AvatarCropper from '@baaligames/shared-ui/components/AvatarCropper';
import Icon from '@baaligames/shared-ui/components/Icon';

type ToastTone = 'ok' | 'err';
type Toast = { tone: ToastTone; msg: string } | null;

export default function ProfilePanel() {
  const { admin, refresh, logout } = useAdminAuth();
  const nav = useNavigate();
  const [toast, setToast] = useState<Toast>(null);
  const [cropping, setCropping] = useState(false);

  // Identity form
  const [username, setUsername] = useState(admin?.username ?? '');
  const [email,    setEmail]    = useState(admin?.email ?? '');
  // 2026-05-18 — Display name fields. Admins + agents both have
  // first_name + last_name; agents additionally carry birthdate.
  // Backend validates (min 2 chars; birthdate YYYY-MM-DD).
  const [firstName, setFirstName] = useState(admin?.first_name ?? '');
  const [lastName,  setLastName]  = useState(admin?.last_name  ?? '');
  const [birthdate, setBirthdate] = useState(admin?.birthdate  ?? '');
  const [savingId, setSavingId] = useState(false);

  // Password form
  const [curPw,    setCurPw]    = useState('');
  const [newPw,    setNewPw]    = useState('');
  const [newPw2,   setNewPw2]   = useState('');
  const [savingPw, setSavingPw] = useState(false);

  // Avatar
  const [uploading, setUploading] = useState(false);

  const flash = (tone: ToastTone, msg: string) => {
    setToast({ tone, msg });
    setTimeout(() => setToast(null), 3000);
  };

  if (!admin) return <p className="text-arena-600 dark:text-neon-200/60 text-sm">Not signed in.</p>;
  const me = admin;

  async function uploadCropped(rawFile: File) {
    setUploading(true);
    try {
      const file = await compressForUpload(rawFile, { preset: 'avatar' });
      const fd = new FormData();
      fd.append('image', file);
      await adminApi.postForm('/admin/auth/me/avatar', fd);
      await refresh();
      flash('ok', 'Photo updated');
    } catch (err) {
      flash('err', (err as ApiError).message || 'Upload failed');
      throw err;
    } finally {
      setUploading(false);
    }
  }

  async function onRemoveAvatar() {
    if (!confirm('Remove your profile photo?')) return;
    setUploading(true);
    try {
      await adminApi.del('/admin/auth/me/avatar');
      await refresh();
      flash('ok', 'Photo removed');
    } catch (err) {
      flash('err', (err as ApiError).message || 'Failed');
    } finally { setUploading(false); }
  }

  async function onSaveIdentity(e: FormEvent) {
    e.preventDefault();
    const unchanged =
      username === me.username &&
      email    === me.email &&
      firstName === (me.first_name ?? '') &&
      lastName  === (me.last_name  ?? '') &&
      birthdate === (me.birthdate  ?? '');
    if (unchanged) {
      flash('ok', 'No changes');
      return;
    }
    // Client-side guard mirroring the backend Zod schema. The DB's
    // CHECK constraint rejects <2-char first/last too.
    if (firstName && firstName.trim().length < 2) { flash('err', 'First name must be at least 2 characters'); return; }
    if (lastName  && lastName.trim().length  < 2) { flash('err', 'Last name must be at least 2 characters');  return; }
    if (isAgent && birthdate && !/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) {
      flash('err', 'Birthdate must be in YYYY-MM-DD format'); return;
    }
    setSavingId(true);
    try {
      const body: Record<string, string> = {};
      if (username !== me.username) body.username = username.trim();
      if (email    !== me.email)    body.email    = email.trim();
      if (firstName !== (me.first_name ?? '')) body.first_name = firstName.trim();
      if (lastName  !== (me.last_name  ?? '')) body.last_name  = lastName.trim();
      if (isAgent && birthdate !== (me.birthdate ?? '')) body.birthdate = birthdate;
      await adminApi.put('/admin/auth/me', body);
      await refresh();
      flash('ok', 'Profile updated');
    } catch (err) {
      flash('err', (err as ApiError).message || 'Update failed');
    } finally { setSavingId(false); }
  }

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    if (newPw !== newPw2) { flash('err', 'New passwords do not match'); return; }
    if (newPw.length < 8) { flash('err', 'Password must be ≥ 8 characters'); return; }
    if (!/[A-Za-z]/.test(newPw) || !/\d/.test(newPw)) {
      flash('err', 'Password must contain at least one letter and one number'); return;
    }
    setSavingPw(true);
    try {
      await adminApi.post('/admin/auth/change-password', {
        currentPassword: curPw, newPassword: newPw,
      });
      setCurPw(''); setNewPw(''); setNewPw2('');
      flash('ok', 'Password changed — sign in again to refresh sessions');
      setTimeout(async () => { await logout(); nav('/login', { replace: true }); }, 1200);
    } catch (err) {
      flash('err', (err as ApiError).message || 'Change failed');
    } finally { setSavingPw(false); }
  }

  async function onSignOut() {
    await logout();
    nav(me.role === 'agent' ? '/agent/login' : '/login', { replace: true });
  }

  const isAgent = me.role === 'agent';

  return (
    // Phase 4 (2026-05-15): no outer max-w wrapper, no duplicate "My profile"
    // page header — AccountHub provides both. Just the section stack.
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px]
            ${toast.tone === 'ok'
              ? 'bg-emerald-50 ring-emerald-200/60 text-emerald-800 dark:bg-emerald-500/10 dark:ring-emerald-400/30 dark:text-emerald-200'
              : 'bg-rose-50 ring-rose-200/60 text-rose-800 dark:bg-rose-500/10 dark:ring-rose-400/30 dark:text-rose-200'}`}>
          {toast.msg}
        </motion.div>
      )}

      {/* Hero card — gradient is the page's signature, kept intact.
          Role-tinted: emerald for agent, violet for admin. */}
      <motion.section
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className={`relative overflow-hidden rounded-2xl p-5
                    bg-gradient-to-br ${isAgent
                      ? 'from-emerald-500 to-teal-600 ring-emerald-300/40 shadow-[0_10px_30px_-12px_rgba(16,185,129,0.55)]'
                      : 'from-violet-500 to-fuchsia-600 ring-violet-300/40 shadow-[0_10px_30px_-12px_rgba(139,92,246,0.55)]'}
                    ring-1 text-white`}>
        <div aria-hidden className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-white/15 blur-2xl" />
        <div className="relative flex items-center gap-4">
          <div className="relative">
            <AdminAvatar url={me.avatar_url} username={me.username} size="xl" ring />
            <button
              onClick={() => setCropping(true)}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 grid place-items-center w-9 h-9 rounded-full
                         bg-white text-arena-900 hover:bg-arena-50
                         shadow-lg disabled:opacity-50 active:scale-95 transition"
              title="Change photo"
            >
              <Icon name={uploading ? 'progress_activity' : 'photo_camera'}
                    size={18} className={uploading ? 'animate-spin' : ''} />
            </button>
          </div>
          <div className="min-w-0 flex-1">
            {/* 2026-05-18 — Show the structured display name as the
                primary identity (composed from first/last). Username
                stays beneath it as a low-key secondary handle for
                self-identification (the self view of @yourname is
                not a peer leak). When first/last are still missing,
                only @username surfaces here so the user has a
                non-empty header — the "Complete your profile" modal
                will then nudge them. */}
            <h2 className="text-xl font-bold truncate">
              {((me.first_name || '') + ' ' + (me.last_name || '')).trim() || `@${me.username}`}
            </h2>
            <p className="text-[12.5px] opacity-90 truncate">
              {((me.first_name || '') + (me.last_name || '')).trim() ? `@${me.username} · ${me.email}` : me.email}
            </p>
            <span className="mt-2 inline-flex items-center gap-1 rounded-full
                             bg-white/20 ring-1 ring-white/25 px-2 py-0.5
                             text-[10px] font-bold uppercase tracking-wider">
              <Icon name={isAgent ? 'support_agent' : 'shield'} size={12} filled />
              {isAgent ? 'Agent' : 'Platform admin'}
            </span>
          </div>
        </div>
        {me.avatar_url && (
          <button onClick={onRemoveAvatar} disabled={uploading}
                  className="absolute top-3 right-3 text-[11px] font-semibold opacity-85 hover:opacity-100 disabled:opacity-50">
            Remove photo
          </button>
        )}
      </motion.section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Identity */}
        <Card icon="badge" title="Identity" subtitle={isAgent
          ? 'Your display name (peers see this), username, email, and date of birth'
          : 'Your display name (agents see this), username and email'}>
          <form onSubmit={onSaveIdentity} className="space-y-3">
            {/* 2026-05-18 — Display name fields. Peers see these;
                username stays login-only. Birthdate is agent-only. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="First name" value={firstName} onChange={setFirstName} icon="person" hint="Shown to your downline / upline" />
              <Field label="Last name"  value={lastName}  onChange={setLastName}  icon="person" hint="Shown to your downline / upline" />
            </div>
            {isAgent && (
              <Field label="Date of birth" value={birthdate} onChange={setBirthdate} icon="cake" type="date" hint="YYYY-MM-DD · private (never shown to peers)" />
            )}
            <Field label="Username" value={username} onChange={setUsername} icon="person" hint="Login only — never displayed to peers" />
            <Field label="Email"    value={email}    onChange={setEmail}    icon="mail" type="email" />
            <button
              type="submit" disabled={savingId}
              className={`w-full inline-flex items-center justify-center gap-1.5
                          px-4 py-2.5 rounded-full
                          ${isAgent
                            ? 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 shadow-[0_2px_10px_rgba(16,185,129,0.4)]'
                            : 'bg-violet-600 hover:bg-violet-500 active:bg-violet-700 shadow-[0_2px_10px_rgba(139,92,246,0.4)]'}
                          text-white text-[13px] font-bold
                          transition active:scale-95 disabled:opacity-50`}>
              {savingId ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
              {savingId ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        </Card>

        {/* Password */}
        <Card icon="lock" title="Change password" subtitle="Signs you out of all other devices">
          <form onSubmit={onChangePassword} className="space-y-3">
            <Field label="Current password" value={curPw}  onChange={setCurPw}  icon="lock" type="password" />
            <Field label="New password"     value={newPw}  onChange={setNewPw}  icon="key"  type="password" hint="≥ 8 chars · letter + digit" />
            <Field label="Confirm new"      value={newPw2} onChange={setNewPw2} icon="key"  type="password" />
            <button
              type="submit" disabled={savingPw || !curPw || !newPw}
              className="w-full inline-flex items-center justify-center gap-1.5
                         px-4 py-2.5 rounded-full
                         bg-rose-600 hover:bg-rose-500 active:bg-rose-700
                         text-white text-[13px] font-bold
                         shadow-[0_2px_10px_rgba(244,63,94,0.4)]
                         transition active:scale-95 disabled:opacity-50">
              {savingPw ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="lock_reset" size={14} />}
              {savingPw ? 'Updating…' : 'Change password'}
            </button>
          </form>
        </Card>
      </div>

      {/* Account info */}
      <Card icon="info" title="Account info" subtitle="Read-only platform metadata">
        <dl className="space-y-1">
          <Info k="Member since"   v={me.created_at ? new Date(me.created_at).toLocaleDateString() : '—'} />
          <Info k="Last login"     v={me.last_login_at ? new Date(me.last_login_at).toLocaleString() : 'first time'} />
          <Info k="Last login IP"  v={me.last_login_ip || '—'} />
          {me.role === 'agent' && me.parent_admin_id && (
            <Info k="Reports to" v={`Admin #${me.parent_admin_id}`} />
          )}
        </dl>
      </Card>

      {/* Sign out */}
      <div className="pb-1">
        <button
          onClick={onSignOut}
          className="w-full inline-flex items-center justify-center gap-2 rounded-2xl
                     bg-rose-50 ring-1 ring-inset ring-rose-200/60 text-rose-700
                     hover:bg-rose-100
                     dark:bg-rose-500/10 dark:ring-rose-400/30 dark:text-rose-300
                     dark:hover:bg-rose-500/20
                     py-3 font-semibold transition">
          <Icon name="logout" size={18} /> Sign out
        </button>
      </div>

      {cropping && (
        <AvatarCropper
          title="Adjust your photo"
          onUpload={uploadCropped}
          onClose={() => setCropping(false)}
        />
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

function Card({ icon, title, subtitle, children }: {
  icon: string; title: string; subtitle?: string; children: React.ReactNode;
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl p-5
                 bg-white dark:bg-arena-700/40
                 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                 backdrop-blur">
      <h3 className="text-[14px] font-semibold text-arena-900 dark:text-white inline-flex items-center gap-2 mb-1">
        <Icon name={icon} size={16} className="text-arena-600 dark:text-neon-200/60" /> {title}
      </h3>
      {subtitle && <p className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mb-3">{subtitle}</p>}
      {children}
    </motion.section>
  );
}

function Field({ label, value, onChange, icon, type = 'text', hint }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  icon: string;
  type?: string;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 mb-1 font-semibold">{label}</span>
      <span className="relative block">
        <Icon name={icon} size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-500 dark:text-neon-200/40 pointer-events-none" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // 2026-05-18 — Date inputs cap at 12rem (≈192px) on mobile so
          // the fixed-length DD/MM/YYYY field doesn't sprawl across
          // the row. Other input types stay full-width.
          className={`w-full ${type === 'date' ? 'max-w-[10rem] sm:max-w-[12rem]' : ''} rounded-xl pl-9 pr-3 py-2 text-[14px]
                     bg-white dark:bg-arena-800/60
                     ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                     text-arena-900 dark:text-white
                     placeholder-arena-400 dark:placeholder-neon-200/40
                     focus:outline-none focus:ring-2 focus:ring-violet-400/60`}
        />
      </span>
      {hint && <span className="mt-1 block text-[10.5px] text-arena-600 dark:text-neon-200/50">{hint}</span>}
    </label>
  );
}

function Info({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b last:border-b-0
                    border-arena-200/40 dark:border-arena-700/40">
      <dt className="text-[12px] text-arena-600 dark:text-neon-200/60">{k}</dt>
      <dd className="text-[13px] font-semibold text-arena-900 dark:text-white truncate">{v}</dd>
    </div>
  );
}
